import React, { useEffect, useId, useState } from 'react';
import {
  formatLimitedDesignationA11yLabel,
  formatLimitedDesignationOrdinal,
  formatLimitedDesignationIssuedDate,
} from '../../utils/limitedDesignationLabels';

export interface LimitedDesignationViewModel {
  public_id: string;
  name: string;
  ordinal: number;
  edition_size: number;
  ordinal_label?: string;
  title?: string;
  issuer_label?: string;
  public_blurb?: string | null;
  description?: string | null;
  issued_at?: string;
  authority_class?: string;
  status?: string;
}

type PlateSize = 'compact' | 'card' | 'full';

interface Props {
  designation: LimitedDesignationViewModel;
  size?: PlateSize;
  className?: string;
  onAnalyticsOpen?: () => void;
}

export const LimitedDesignationPlate: React.FC<Props> = ({
  designation,
  size = 'full',
  className = '',
  onAnalyticsOpen,
}) => {
  const [open, setOpen] = useState(false);
  const titleId = useId();
  const ordinal =
    designation.ordinal_label ||
    formatLimitedDesignationOrdinal(designation.ordinal, designation.edition_size);
  const issuer = designation.issuer_label || 'Bilt:Spec';
  const a11y = formatLimitedDesignationA11yLabel(
    designation.name,
    designation.ordinal,
    designation.edition_size,
    issuer
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const openDetail = () => {
    setOpen(true);
    onAnalyticsOpen?.();
  };

  const platePad =
    size === 'card' ? 'px-2.5 py-1.5' : size === 'compact' ? 'px-2 py-1' : 'px-3.5 py-2.5';
  const nameSize = size === 'card' || size === 'compact' ? 'text-[9px]' : 'text-[10px]';
  const ordSize = size === 'card' || size === 'compact' ? 'text-xs' : 'text-lg sm:text-xl';

  return (
    <>
      <button
        type="button"
        onClick={openDetail}
        aria-label={a11y}
        className={`group inline-flex flex-col items-start rounded-sm border border-white/20 bg-gradient-to-b from-[#2a2c2f] to-[#1a1b1d] text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] transition hover:border-orange-500/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/70 ${platePad} ${className}`}
      >
        <span
          className={`${nameSize} font-semibold uppercase tracking-[0.18em] text-gray-300 group-hover:text-orange-100/90`}
        >
          {designation.name}
        </span>
        <span className={`${ordSize} font-mono font-semibold tracking-wide text-white tabular-nums`}>
          {ordinal}
        </span>
        {size !== 'card' && size !== 'compact' ? (
          <span className="mt-0.5 text-[10px] uppercase tracking-[0.14em] text-gray-500">
            {issuer} Official
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/70 p-4"
          role="presentation"
          onClick={() => setOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className="w-full max-w-md rounded-2xl border border-white/15 bg-[#121314] p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rounded-sm border border-white/20 bg-gradient-to-b from-[#2a2c2f] to-[#1a1b1d] px-4 py-3 mb-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-300">
                {designation.name}
              </p>
              <p id={titleId} className="text-2xl font-mono font-semibold text-white tabular-nums">
                {ordinal}
              </p>
              <p className="text-[10px] uppercase tracking-[0.14em] text-gray-500">
                {issuer} Official
              </p>
            </div>
            <p className="text-sm text-gray-300 leading-relaxed">
              {designation.public_blurb ||
                designation.description ||
                'Recognized as part of a limited Bilt:Spec designation.'}
            </p>
            {designation.issued_at ? (
              <p className="mt-3 text-xs text-gray-500">
                Issued {formatLimitedDesignationIssuedDate(designation.issued_at) || designation.issued_at}
              </p>
            ) : null}
            <p className="mt-1 text-xs text-gray-500">Verified limited designation. Not a marketplace listing.</p>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                className="rounded-lg border border-white/15 px-3 py-1.5 text-sm text-gray-200 hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/70"
                onClick={() => setOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default LimitedDesignationPlate;
